import {
  EntityOperation,
  logEntityOperationFailure,
} from 'serviceWorker/helpers/entityFailureLog';
import type { PendingPublish } from './pendingPublish';
import { Services } from 'shared/domain/messages/message';
import type { PullingContract } from 'serviceWorker/helpers/broadcastPullError';
import { ServiceData } from 'serviceWorker/db/service';
import { debugLog } from 'shared/logger/debugLog';
import {
  FailedPull,
  HeadersData,
  NormalizedEntityResponseData,
  Pull,
  ResponseWithSyncKey,
} from './types';
import { getFinishedServiceStateWithoutSyncKey } from './getFinishedServiceState';
import { Identificable } from 'shared/types/commonView';
import { handlePullFailure } from './handlePullFailure';
import { captureCollectionScope } from 'serviceWorker/helpers/dataScope';

interface MakePullWithSyncKeyEntityProps {
  fetch: (
    data: HeadersData,
    abortController: AbortController
  ) => Promise<NormalizedEntityResponseData | Identificable[]> | undefined;
  channelContract: PullingContract;
  entityRepository: any;
  entityService: any;
  entityName: Services;
  finishedServiceStateFactory: (
    total: number,
    syncKey: string
  ) => Partial<ServiceData>;
  /** The collection event asked for by syncs, published once the download is stored. */
  pendingCollection: PendingPublish;
  withoutSyncKey?: boolean;
  /**
   * A failed download keeps the stored items readable instead of clearing them:
   * the collection is downloaded again on reads, so a failed request (API error,
   * network) must not empty what the user already sees.
   */
  keepStoredOnFailure?: boolean;
}

export function makePullEntity({
  fetch,
  channelContract,
  entityRepository: storedEntities,
  entityService: storedService,
  entityName,
  finishedServiceStateFactory,
  pendingCollection,
  withoutSyncKey,
  keepStoredOnFailure,
}: MakePullWithSyncKeyEntityProps): Pull {
  return async (data, abortController, callerScope): Promise<void | FailedPull> => {
    // Every write of the pull goes through its scope: none reaches data that became outdated meanwhile.
    const scope = callerScope ?? captureCollectionScope(storedService, data.projectId, entityName);
    const entityService = {
      add: scope.guard(storedService.add),
      stopDownloading: scope.guard(storedService.stopDownloading),
    };
    const entityRepository = {
      get: scope.guard(storedEntities.get),
      clear: scope.guard(storedEntities.clear),
      addBatch: scope.guard(storedEntities.addBatch),
    };
    const stopDownloadingOnly = async (): Promise<void> => {
      await entityService.stopDownloading();
    };

    const hardResetServiceState = async (): Promise<void> => {
      await entityService.add(
        useSyncKey
          ? {
              total: undefined,
              isDownloading: false,
              isDownloaded: false,
              syncKey: undefined,
            }
          : {
              total: undefined,
              isDownloading: false,
              isDownloaded: false,
            }
      );
    };

    /** Stops the download and keeps the stored items, downloaded as long as there are any. */
    const keepStoredAfterFailure = async (): Promise<void> => {
      const storedItems = await entityRepository.get();
      const storedCount = Array.isArray(storedItems) ? storedItems.length : 0;
      await entityService.add({ total: storedCount, isDownloading: false, isDownloaded: storedCount > 0 });
    };

    debugLog('pulling entityName: ', entityName);
    const useSyncKey = !withoutSyncKey;
    let promise:
      | Promise<NormalizedEntityResponseData | Identificable[]>
      | undefined;

    try {
      promise = fetch(data, abortController);
    } catch (e: unknown) {
      logEntityOperationFailure(EntityOperation.FETCHING, {
        entityName,
        projectId: data.projectId,
        error: e,
      });
      await stopDownloadingOnly();
      return;
    }

    if (!promise) {
      await stopDownloadingOnly();
      return;
    }

    await entityService.add({
      total: undefined,
      isDownloading: true,
      isDownloaded: false,
      syncKey: undefined,
    });
    return promise
      .then(
        async (res: NormalizedEntityResponseData | Identificable[]) => {
          if (abortController.signal.aborted) {
            await stopDownloadingOnly();
            return;
          }
          if (!res) {
            await entityService.add({
              isDownloading: false,
            });
            return;
          }
          await entityService.add({
            total: useSyncKey
              ? (res as ResponseWithSyncKey).items.length
              : (res as Identificable[]).length,
            isDownloading: true,
          });

          if (abortController.signal.aborted) {
            await stopDownloadingOnly();
            return;
          }
          await entityRepository.addBatch(
            useSyncKey ? (res as ResponseWithSyncKey).items : res
          );
          const syncKey =
            useSyncKey && ((res as ResponseWithSyncKey).syncKey);

          const entityServiceData = syncKey
            ? finishedServiceStateFactory(
                (res as ResponseWithSyncKey).items.length,
                syncKey
              )
            : getFinishedServiceStateWithoutSyncKey(
                (res as Identificable[]).length
              );

          // for some reason getting all data makes IndexedDB faster next time
          const start = Date.now();
          debugLog(`start ${entityName} getAll:`, start);
          await entityRepository.get();
          await entityService.add(entityServiceData);
          const end = Date.now();
          debugLog(`entityRepository ${entityName} time:`, end - start);
          await scope.write(async () => pendingCollection.flush());
        }
      )
      .catch((error: unknown) =>
        handlePullFailure({
          error,
          abortController,
          entityName,
          projectId: data.projectId,
          channelContract,
          scope,
          stopDownloading: stopDownloadingOnly,
          resetStoredData: keepStoredOnFailure
            ? keepStoredAfterFailure
            : async () => {
                await entityRepository.clear();
                await hardResetServiceState();
              },
        })
      );
  };
}
